import { Load } from "components/load";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Meta } from "src/@types/paginated";
import Table from "../components/table";
import { useJustificationFilterActions } from "../ducks/filter/hooks/actions";
import { useFilterState } from "../ducks/filter/hooks/filterState";
import { columns } from "./columns";
import PageError from "components/page-error";
import { handeOrderBy } from "../../../../utils/pagination";
import { useNavigate } from "react-router-dom";
import {
  useGetRegionalsQuery,
  useDeactivateRegionalMutation,
} from "../../../../services/regional";
import { AddMultipleRegionalModal } from "../create-multiple";
import { IRegional } from "src/@types/regional";
import { toast } from "components/ui/use-toast";
import { REGIONAL_TEXTS } from "../utils/consts";
import { MESSAGES } from "../utils/messages";
import { StatusEnum } from "@/services/dtos/user.dto";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { ConfirmDeactivateContent } from "../components/confirm-deactivate-dialog";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";

export const Justification: React.FC = () => {
  const navigate = useNavigate();
  const { updateFilter } = useJustificationFilterActions();
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };
  const orderBy = handeOrderBy(filters.order);

  const [statusFilter, setStatusFilter] = useState<StatusEnum>("ACTIVE");

  const { handleModal } = useDialogModal();

  const [deactivateRegional, { isLoading: isDeactivating }] =
    useDeactivateRegionalMutation();

  const { data, isLoading, isFetching, error } = useGetRegionalsQuery({
    search: filters.search,
    orderBy: JSON.stringify(orderBy),
    meta: {
      page: filters?.meta?.page || 1,
    },
    status: statusFilter,
  });

  const { meta, data: regional } = data || { meta: {}, data: [] };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateFilter({ key: "search", value: event.target.value });
  };

  const debouncedHandleInputChange = useCallback(
    debounce(handleInputChange, 500),
    [],
  );

  const load = isLoading;

  useEffect(() => {
    updateFilter({ key: "search", value: "" });
  }, []);

  const handleOpenDeactivateDialog = (regionalRow: IRegional) => {
    handleModal({
      isOpen: true,
      title: (
        <div className="flex items-center gap-3 text-2xl font-extrabold text-red-700">
          <AlertCircle className="h-7 w-7 flex-shrink-0" />
          Confirmação Necessária
        </div>
      ),
      element: (
        <ConfirmDeactivateContent
          regionalName={regionalRow.name}
          isLoading={isDeactivating}
          onConfirm={() => handleConfirmDeactivate(regionalRow)}
          onCancel={() => handleModal({ isOpen: false })}
        />
      ),
    });
  };

  const handleConfirmDeactivate = async (selectedRegional: IRegional) => {
    if (!selectedRegional) return;

    try {
      const result = await deactivateRegional(+selectedRegional.id).unwrap();

      if (result.data.unlinked_count > 0) {
        const stores = result.data.unlinked_stores;

        const storesList = stores
          .slice(0, REGIONAL_TEXTS.MAX_STORES_TO_SHOW)
          .map((store: any) => `${store.code} - ${store.name}`)
          .join(", ");

        const remaining = stores.length - REGIONAL_TEXTS.MAX_STORES_TO_SHOW;
        const storesText =
          remaining > 0 ? `${storesList} e mais ${remaining}` : storesList;

        toast({
          ...MESSAGES.DEACTIVATE.SUCCESS,
          description: `${result.data.unlinked_count} ${
            result.data.unlinked_count === 1
              ? "loja desvinculada"
              : "lojas desvinculadas"
          }: ${storesText}`,
        });
      } else {
        toast(MESSAGES.DEACTIVATE.SUCCESS);
      }

      handleModal({ isOpen: false });
    } catch (error: any) {
      console.error(error);

      toast({
        ...MESSAGES.DEACTIVATE.ERROR,
        description:
          error?.data?.message || MESSAGES.DEACTIVATE.ERROR.description,
      });
    }
  };

  if (load) {
    return <Load />;
  }

  if (error) {
    return (
      <PageError status={500} message={REGIONAL_TEXTS.ERROR_INTERNAL_SERVER} />
    );
  }

  return (
    <>
      <PageLayout>
        <div className="flex justify-start w-full items-center gap-4">
          <ArrowLeft
            className="cursor-pointer"
            onClick={() => navigate("/conciliacao/lojas")}
          />
          <PageTitle
            title={REGIONAL_TEXTS.PAGE_TITLE}
            description={REGIONAL_TEXTS.PAGE_SUBTITLE}
          />
        </div>
        <ContentContainer>
          <div className="flex flex-col gap-[11px]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
              {/* Input de busca */}
              <div className="w-full">
                <Input
                  type="text"
                  onChange={debouncedHandleInputChange}
                  placeholder={REGIONAL_TEXTS.SEARCH_PLACEHOLDER}
                  className="h-8 bg-white"
                />
              </div>

              {/* Select de Status */}
              <div className="w-full">
                <Select
                  value={statusFilter}
                  onValueChange={(value: StatusEnum) => setStatusFilter(value)}
                >
                  <SelectTrigger className="h-8 bg-white">
                    <SelectValue
                      placeholder={REGIONAL_TEXTS.STATUS_FILTER_PLACEHOLDER}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">
                      {REGIONAL_TEXTS.STATUS_ACTIVE}
                    </SelectItem>
                    <SelectItem value="INACTIVE">
                      {REGIONAL_TEXTS.STATUS_INACTIVE}
                    </SelectItem>
                    <SelectItem value="ALL">
                      {REGIONAL_TEXTS.STATUS_ALL}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Botão Adicionar */}
              <div className="w-full flex justify-end">
                <AddMultipleRegionalModal />
              </div>
            </div>

            <Table
              columns={columns(handleOpenDeactivateDialog)}
              meta={meta as Meta}
              data={regional}
              isFetching={isFetching}
            />
          </div>
        </ContentContainer>
      </PageLayout>
    </>
  );
};
